import { Button } from "@/components/ui/button";
import { ArrowRight, Eye } from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";
import pauloAvatar from "@/assets/paulo-avatar.png";
import { useLanguage } from "@/contexts/LanguageContext";

const Hero = () => {
  const { t } = useLanguage();
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[calc(100vh-5rem)] sm:min-h-[calc(100vh-6rem)] lg:min-h-[calc(100vh-7rem)] pt-20 sm:pt-24 lg:pt-28 flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="max-w-3xl animate-fade-in">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-medium shadow-glow">
                {t("hero.badge")}
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              {t("hero.name")}
            </h1>
            
            <h2 className="text-2xl md:text-3xl text-primary mb-6 font-semibold">
              {t("hero.title")}
            </h2>
            
            <p className="text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl">
              {t("hero.description")}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => scrollToSection("contact")}
                size="lg"
                className="gradient-accent shadow-glow group"
              >
                {t("hero.cta")}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection("projects")}
                size="lg"
                variant="outline"
                className="border-primary/40 hover:border-primary hover:bg-primary/10 hover:shadow-glow"
              >
                <Eye className="mr-2 h-4 w-4" />
                {t("hero.viewProjects")}
              </Button>
            </div>
          </div>

          {/* Avatar */}
          <div className="flex justify-center md:justify-end animate-fade-in">
            <div className="relative group">
              <div className="absolute inset-0 gradient-premium opacity-20 blur-3xl rounded-full animate-pulse" />
              <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-primary/20 shadow-glow transition-all duration-500 group-hover:scale-105 group-hover:border-primary/40 group-hover:shadow-[0_0_60px_hsl(var(--primary)/0.5)]">
                <img 
                  src={pauloAvatar} 
                  alt="Paulo Henrique - UX Writer & Conversational Designer"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-primary/40 rounded-full flex justify-center p-2">
          <div className="w-1.5 h-3 bg-primary rounded-full animate-glow" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
